"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DocumentVersionSchema = exports.DocumentSchema = exports.FolderSchema = exports.FOLDER_COLORS = exports.DocumentBlockSchema = exports.BLOCK_ALIGNMENTS = exports.OWNER_SCOPES = exports.CHANGE_TYPES = exports.DEFAULT_CATEGORY_IDS = exports.DEFAULT_DOCUMENT_CATEGORIES = exports.DocumentCategoryRecordSchema = exports.CATEGORY_APPLIES_TO = exports.DocumentCategoryIdSchema = exports.DOCUMENT_CATEGORIES = exports.DOCUMENT_DISCHARGES = exports.DOCUMENT_KINDS = exports.VERSION_STATUSES = exports.BLOCK_TYPES = void 0;
exports.dischargeOf = dischargeOf;
exports.dischargeConflict = dischargeConflict;
exports.categoriesForKind = categoriesForKind;
exports.slugifyCategoryId = slugifyCategoryId;
exports.requiredMergeFields = requiredMergeFields;
const zod_1 = require("zod");
const documentAcceptance_1 = require("./documentAcceptance");
const inlineRuns_1 = require("./inlineRuns");
// One-way: neither of these imports anything from here, so this cannot cycle.
const documentDelta_1 = require("./documentDelta");
const sectionNumbering_1 = require("./sectionNumbering");
const context_1 = require("./mergeProviders/context");
/**
 * Legal documents: folders, documents, and the immutable version chain.
 *
 * A published version is NEVER edited. "Editing" copies its blocks into a new
 * draft v(n+1). Block ids are STABLE across that copy — that is what makes the
 * diff say "Clause 4.2 changed" instead of re-diffing the whole document.
 */
exports.BLOCK_TYPES = [
    'heading', 'paragraph', 'list', 'table', 'callout',
    'mergeField', 'signatureField', 'pageBreak',
];
/**
 * The four STORED statuses. There is deliberately no `superseded` here.
 *
 * A version stays `published` forever; supersession is DERIVED by
 * `isSuperseded()` in ./documentLifecycle. Storing it would need a scheduler to
 * flip the flag at the next version's effective instant, and — worse — it would
 * drop the version out of both `activeVersion()` and `isAcceptanceStale()`,
 * which only walk published versions. Staleness would then silently
 * under-report, and parties who owe re-acceptance would look compliant.
 */
exports.VERSION_STATUSES = [
    'draft', 'in_review', 'approved', 'published',
];
exports.DOCUMENT_KINDS = ['policy', 'agreement'];
/**
 * HOW A DOCUMENT IS DISCHARGED — what the reader has to DO about it.
 *
 * `signature` — they sign it. `accept_decline` — they are shown it and answer
 * accept or decline; there is no signature, and declining is a real answer
 * rather than a failure to finish.
 *
 * WHY THIS IS NOT `kind`. `kind` says what a document IS, and the two were
 * welded together: an agreement was signed, a policy was accepted, and neither
 * could be anything else. That held until a checkout had to present both in one
 * packet — a return policy the merchant must positively accept, beside a lease
 * they sign — and until an operator wanted a policy that genuinely is signed.
 * Separating them lets the author decide, so every document can travel in the
 * same envelope and be discharged the way it actually should be.
 */
exports.DOCUMENT_DISCHARGES = ['signature', 'accept_decline'];
/**
 * THE DISCHARGE A DOCUMENT ACTUALLY HAS.
 *
 * Every document written before this field existed has none, and falls back to
 * exactly what its `kind` used to imply — so nothing migrates and no existing
 * document changes behaviour. Read through here rather than off the row, so the
 * fallback is stated once instead of in every caller that ever asks.
 */
function dischargeOf(document, 
/**
 * Whether the CONTENT carries a signature block. Optional, because most
 * callers only have the document row — but where the body is to hand, a
 * signature block drawn into it is the author saying "this is signed", and it
 * outranks the kind. That is the "add a signature block and it becomes a
 * signed document" behaviour, and it falls out of the fallback rather than
 * needing a write.
 *
 * An EXPLICIT `discharge` still wins over both. An author who has said what
 * they want is not overruled by what they drew — the disagreement between the
 * two is caught at publish instead (`dischargeConflict`), where it can be
 * named and fixed rather than silently resolved.
 */
hasSignatureBlock) {
    const declared = document?.discharge;
    if (declared === 'signature' || declared === 'accept_decline')
        return declared;
    if (hasSignatureBlock)
        return 'signature';
    return document?.kind === 'agreement' ? 'signature' : 'accept_decline';
}
/**
 * THE ONE COMBINATION THAT CANNOT RENDER: accept/decline WITH a signature block.
 *
 * Asked directly, "what if an author adds both?" sounds like it wants a merge —
 * accept the terms AND sign. It does not. A signature block is a PLACE IN THE
 * PAGE where a signature is drawn; accept/decline never draws one. So a
 * document set to accept/decline that carries a signature block renders a
 * signature line nobody will ever sign — a blank where a party's mark belongs,
 * which is the exact defect the whole renderer refuses elsewhere.
 *
 * The two are also not additive in meaning. Signing IS assent, and a stronger
 * form of it than a click; asking for both makes it ambiguous which act bound
 * the reader. And "refusing" is not what accept/decline uniquely buys — a
 * signature envelope can already be DECLINED (`declinedAt`/`declineReason`),
 * so a signed document loses nothing by not also offering an accept button.
 *
 * Hence: one document, one discharge. Returns the reason to show the author, or
 * null when there is nothing wrong.
 */
function dischargeConflict(document, hasSignatureBlock, signatureBlockCount = hasSignatureBlock ? 1 : 0) {
    if (!hasSignatureBlock)
        return null;
    if (dischargeOf(document, hasSignatureBlock) !== 'accept_decline')
        return null;
    const n = signatureBlockCount;
    return `This document is set to Accept / Decline but contains ${n} signature `
        + `${n === 1 ? 'block' : 'blocks'}. Nobody signs an accepted document, so ${n === 1 ? 'it' : 'they'} `
        + 'would print as a blank signature line. Either switch it to Signed, or remove the '
        + `signature ${n === 1 ? 'block' : 'blocks'}.`;
}
/**
 * WHERE A DOCUMENT IS USED — the axis a picker can be hardcoded against.
 *
 * `kind` says what a document IS (a policy or an agreement) and `folderId` is
 * how an operator files it — free text, renamed and reorganised at will, and so
 * nothing code may key on. Neither answers the question every picker actually
 * asks: "which documents belong HERE?"
 *
 * That is this. The rent screen shows rental agreements and nothing else, and
 * filing a document under the right category is all it takes for it to appear
 * in the right place.
 *
 * THE LIST IS NO LONGER FIXED IN CODE. Categories are rows in documents-service
 * (`CATEGORY#<id>`), so an operator can add "NDA" or retire one that was a
 * mistake without a deploy. What is below is the BUILT-IN SET those rows are
 * seeded from — the ids code is allowed to hardcode against, because a stored
 * row can be renamed or retired but never deleted out from under a screen that
 * names it. See `DEFAULT_DOCUMENT_CATEGORIES`.
 *
 * NEVER REPURPOSE A VALUE. A document already carries the old meaning, and a
 * signature already binds it. New uses get new values; retired ones stay listed
 * so stored rows keep parsing.
 */
exports.DOCUMENT_CATEGORIES = [
    /** Hardware taken on rental — cancel-any-time terms. */
    'device_rental',
    /** Hardware financed over a term. */
    'device_finance',
    /** Hardware bought outright. */
    'device_purchase',
    /** Signed at checkout, whatever is being bought. */
    'checkout',
    /** The right to use the software itself. */
    'software_license',
    /** The recurring plan and what it includes. */
    'subscription',
    /** Signed by a tenant's staff — handbooks, conduct, acknowledgements. */
    'employee',
    /** Signed by the business itself — terms of service, data processing. */
    'tenant',
];
/**
 * A category id: lowercase words joined by underscores, matching the built-ins
 * above. Operator-created ids are slugged into this shape before storage, so
 * every id — built-in or not — reads the same wherever it surfaces.
 */
exports.DocumentCategoryIdSchema = zod_1.z.string().regex(/^[a-z0-9]+(_[a-z0-9]+)*$/);
/**
 * WHICH LIST A CATEGORY BELONGS ON.
 *
 * Agreements are signed and policies are accepted, and an operator filing a
 * cookie policy should not have to scroll past twelve agreement categories to
 * find it. `both` is for the genuinely shared ones — website terms are offered
 * as an agreement on signup and published as a policy on the site.
 */
exports.CATEGORY_APPLIES_TO = ['agreement', 'policy', 'both'];
/**
 * ONE CATEGORY, AS STORED. `builtIn` rows are seeded from
 * `DEFAULT_DOCUMENT_CATEGORIES` and may be relabelled, reordered and retired,
 * but never deleted — code hardcodes those ids, and a deleted id would take a
 * picker's contents with it. Operator-created rows carry `builtIn: false` and
 * can be deleted outright while nothing references them.
 */
exports.DocumentCategoryRecordSchema = zod_1.z.object({
    categoryId: exports.DocumentCategoryIdSchema,
    label: zod_1.z.string().min(1),
    /** What the dropdown says under the label — where this is actually offered. */
    hint: zod_1.z.string().optional(),
    appliesTo: zod_1.z.enum(exports.CATEGORY_APPLIES_TO).default('both'),
    sortOrder: zod_1.z.number().int().default(0),
    builtIn: zod_1.z.boolean().default(false),
    /** Retired categories stay readable — documents still carry the id — but are
     *  offered in no picker. */
    status: zod_1.z.enum(['active', 'retired']).default('active'),
    createdAt: zod_1.z.string().optional(),
    updatedAt: zod_1.z.string().optional(),
});
/**
 * THE SEED SET — what a fresh install starts with, and what documents-service
 * writes on first read of an empty category table.
 *
 * Every id in `DOCUMENT_CATEGORIES` appears here (the eight that predate the
 * table, so nothing filed under them loses its label), plus the general set a
 * business actually needs: partner and internal paperwork on the agreement
 * side, and the public-facing policies — returns, privacy, cookies, website
 * terms — on the policy side.
 *
 * `sortOrder` is in tens so an operator can slot something between two of these
 * without renumbering the rest.
 */
exports.DEFAULT_DOCUMENT_CATEGORIES = [
    // ── Hardware ────────────────────────────────────────────────────────────
    { categoryId: 'device_rental', label: 'Device rental', hint: 'Signed when hardware is rented — cancel-any-time terms', appliesTo: 'agreement', sortOrder: 10, builtIn: true, status: 'active' },
    { categoryId: 'device_finance', label: 'Device finance', hint: 'Signed when hardware is financed over a term', appliesTo: 'agreement', sortOrder: 20, builtIn: true, status: 'active' },
    { categoryId: 'device_purchase', label: 'Device purchase', hint: 'Signed when hardware is bought outright', appliesTo: 'agreement', sortOrder: 30, builtIn: true, status: 'active' },
    { categoryId: 'device_policy', label: 'Device policy', hint: 'Care, use and return of hardware — accepted, not signed', appliesTo: 'policy', sortOrder: 40, builtIn: true, status: 'active' },
    // ── Selling and billing ─────────────────────────────────────────────────
    { categoryId: 'checkout', label: 'Checkout', hint: 'Signed at checkout, whatever is being bought', appliesTo: 'agreement', sortOrder: 50, builtIn: true, status: 'active' },
    { categoryId: 'software_license', label: 'Software licence', hint: 'The right to use the software itself', appliesTo: 'agreement', sortOrder: 60, builtIn: true, status: 'active' },
    { categoryId: 'subscription', label: 'Subscription', hint: 'The recurring plan and what it includes', appliesTo: 'agreement', sortOrder: 70, builtIn: true, status: 'active' },
    { categoryId: 'return_policy', label: 'Returns & refunds', hint: 'What can be returned, by when, and on what terms', appliesTo: 'policy', sortOrder: 80, builtIn: true, status: 'active' },
    // ── The people who sign ─────────────────────────────────────────────────
    { categoryId: 'employee', label: 'Employee agreement', hint: "Signed by a tenant's staff — contracts, conduct, acknowledgements", appliesTo: 'agreement', sortOrder: 90, builtIn: true, status: 'active' },
    { categoryId: 'employee_policy', label: 'Employee policy', hint: 'Handbook, conduct and workplace policies staff accept', appliesTo: 'policy', sortOrder: 100, builtIn: true, status: 'active' },
    { categoryId: 'tenant', label: 'Tenant', hint: 'Signed by the business — terms of service, data processing', appliesTo: 'agreement', sortOrder: 110, builtIn: true, status: 'active' },
    { categoryId: 'partner', label: 'Partner & ISO', hint: 'Signed by resellers, ISOs and referral partners', appliesTo: 'agreement', sortOrder: 120, builtIn: true, status: 'active' },
    // ── Public-facing ───────────────────────────────────────────────────────
    { categoryId: 'website_terms', label: 'Website terms', hint: 'Terms of use accepted by visitors to the website', appliesTo: 'both', sortOrder: 130, builtIn: true, status: 'active' },
    { categoryId: 'privacy_policy', label: 'Privacy policy', hint: 'What personal data is collected and how it is handled', appliesTo: 'policy', sortOrder: 140, builtIn: true, status: 'active' },
    { categoryId: 'cookie_policy', label: 'Cookie policy', hint: 'Cookies and trackers used on the website', appliesTo: 'policy', sortOrder: 150, builtIn: true, status: 'active' },
    // ── Internal company paperwork ──────────────────────────────────────────
    { categoryId: 'internal_company', label: 'Internal company', hint: 'Paperwork inside the business — not offered to customers', appliesTo: 'both', sortOrder: 160, builtIn: true, status: 'active' },
    { categoryId: 'nda', label: 'NDA & confidentiality', hint: 'Signed before anything confidential is shared', appliesTo: 'agreement', sortOrder: 170, builtIn: true, status: 'active' },
    { categoryId: 'vendor', label: 'Vendor & supplier', hint: 'Signed with suppliers, contractors and service providers', appliesTo: 'agreement', sortOrder: 180, builtIn: true, status: 'active' },
    { categoryId: 'security_policy', label: 'Security & data', hint: 'Security, access and data-handling rules', appliesTo: 'policy', sortOrder: 190, builtIn: true, status: 'active' },
    { categoryId: 'general_policy', label: 'General policy', hint: 'Company-wide policies that fit nowhere more specific', appliesTo: 'policy', sortOrder: 200, builtIn: true, status: 'active' },
];
/** Every built-in id, for the membership checks a service does without a read. */
exports.DEFAULT_CATEGORY_IDS = exports.DEFAULT_DOCUMENT_CATEGORIES.map((c) => c.categoryId);
/**
 * The categories offered for one document kind. `both` shows on either list;
 * retired rows show on neither.
 */
function categoriesForKind(categories, kind) {
    return categories
        .filter((c) => c.status !== 'retired' && (c.appliesTo === 'both' || c.appliesTo === kind))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
}
/**
 * An operator-typed name turned into an id. Same shape as the built-ins, so a
 * category added today is indistinguishable from one that shipped with the
 * product. Returns '' when nothing usable survives — the caller rejects that
 * rather than storing an empty id.
 */
function slugifyCategoryId(name) {
    return name.trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 64);
}
/** Deliberately has no default anywhere. Someone must choose. See spec §5.4. */
exports.CHANGE_TYPES = ['minor', 'material'];
exports.OWNER_SCOPES = ['platform', 'tenant'];
const blockId = zod_1.z.string().min(1);
/**
 * Paragraph/heading alignment. OPTIONAL everywhere and absent by default, so
 * all 76 seeded documents keep parsing unchanged — this is purely additive.
 *
 * `'left'` is representable but never written by the editor: Quill emits NO
 * `align` attribute for left (see the LINE-level op-shape tests in
 * super-admin-ui), so left is the absence of a value, not a value. It is kept
 * in the enum only so a document that names it explicitly still validates.
 */
exports.BLOCK_ALIGNMENTS = ['left', 'center', 'right'];
const align = zod_1.z.enum(exports.BLOCK_ALIGNMENTS).optional();
/**
 * `runs` is OPTIONAL everywhere and PRESENTATION ONLY. `text` remains the value
 * `requiredMergeFields` scans and `diffBlocks` compares, so adding or removing a
 * bold span never registers as a wording change in the publish gate.
 *
 * The refine is what stops the two drifting. Without it a formatting edit could
 * quietly rewrite a clause while the diff, which reads `text`, showed nothing.
 */
const HeadingBlock = zod_1.z.object({
    id: blockId, type: zod_1.z.literal('heading'),
    level: zod_1.z.union([zod_1.z.literal(1), zod_1.z.literal(2), zod_1.z.literal(3)]),
    text: zod_1.z.string(),
    runs: zod_1.z.array(inlineRuns_1.InlineRunSchema).optional(),
    align,
}).refine((b) => (0, inlineRuns_1.runsMatchText)(b.runs, b.text), {
    message: 'runs must reproduce text exactly — text is what the diff and the merge-field scanner read',
    path: ['runs'],
});
const ParagraphBlock = zod_1.z.object({
    id: blockId, type: zod_1.z.literal('paragraph'),
    text: zod_1.z.string(),
    runs: zod_1.z.array(inlineRuns_1.InlineRunSchema).optional(),
    align,
}).refine((b) => (0, inlineRuns_1.runsMatchText)(b.runs, b.text), {
    message: 'runs must reproduce text exactly — text is what the diff and the merge-field scanner read',
    path: ['runs'],
});
const ListBlock = zod_1.z.object({
    id: blockId, type: zod_1.z.literal('list'),
    ordered: zod_1.z.boolean().default(false),
    items: zod_1.z.array(zod_1.z.string()),
    /** Parallel to `items`: `itemRuns[i]` formats `items[i]`. Absent means all plain. */
    itemRuns: zod_1.z.array(zod_1.z.array(inlineRuns_1.InlineRunSchema)).optional(),
}).refine((b) => b.itemRuns === undefined || b.itemRuns.length === b.items.length, { message: 'itemRuns must have exactly one entry per item', path: ['itemRuns'] }).refine((b) => b.itemRuns === undefined || b.items.every((t, i) => (0, inlineRuns_1.runsMatchText)(b.itemRuns[i], t)), { message: 'each itemRuns entry must reproduce its item exactly', path: ['itemRuns'] });
const TableBlock = zod_1.z.object({
    id: blockId, type: zod_1.z.literal('table'),
    headers: zod_1.z.array(zod_1.z.string()), rows: zod_1.z.array(zod_1.z.array(zod_1.z.string())),
});
const CalloutBlock = zod_1.z.object({
    id: blockId, type: zod_1.z.literal('callout'),
    tone: zod_1.z.enum(['info', 'warning', 'danger']).default('info'),
    text: zod_1.z.string(),
    runs: zod_1.z.array(inlineRuns_1.InlineRunSchema).optional(),
}).refine((b) => (0, inlineRuns_1.runsMatchText)(b.runs, b.text), {
    message: 'runs must reproduce text exactly — text is what the diff and the merge-field scanner read',
    path: ['runs'],
});
const MergeFieldBlock = zod_1.z.object({
    id: blockId, type: zod_1.z.literal('mergeField'),
    field: zod_1.z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]*$/), label: zod_1.z.string().optional(),
});
const SignatureFieldBlock = zod_1.z.object({
    id: blockId, type: zod_1.z.literal('signatureField'),
    signerRole: zod_1.z.string().min(1), label: zod_1.z.string().optional(),
    /** Printed-name line. Defaults ON — a signature without a printed name is hard to attribute. */
    showName: zod_1.z.boolean().default(true),
    showTitle: zod_1.z.boolean().default(false),
    /** Defaults ON — an undated signature invites a dispute about when it was given. */
    showDate: zod_1.z.boolean().default(true),
    nameLabel: zod_1.z.string().optional(),
    titleLabel: zod_1.z.string().optional(),
    dateLabel: zod_1.z.string().optional(),
});
const PageBreakBlock = zod_1.z.object({ id: blockId, type: zod_1.z.literal('pageBreak') });
/**
 * `z.discriminatedUnion` cannot take refined members — `.refine()` returns a
 * `ZodEffects`, which is not a valid discriminated-union option. HeadingBlock,
 * ParagraphBlock, CalloutBlock and ListBlock all carry refines now (the
 * runs/text invariant), so this is a plain union. Error messages are less
 * precise than a discriminated union's, which is an accepted cost for
 * enforcing the invariant at the schema boundary.
 */
exports.DocumentBlockSchema = zod_1.z.union([
    HeadingBlock, ParagraphBlock, ListBlock, TableBlock,
    CalloutBlock, MergeFieldBlock, SignatureFieldBlock, PageBreakBlock,
]);
/**
 * `{{fieldName}}` with optional inner whitespace — the SAME pattern the
 * renderer resolves (documents-service `src/render/merge.ts`). Names are
 * camelCase identifiers; dots are deliberately not matched, so a nested-path
 * typo shows up as an unresolved placeholder rather than a silent skip.
 */
const MERGE_TOKEN = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;
const tokensIn = (text, into) => {
    for (const m of text.matchAll(MERGE_TOKEN))
        into.add(m[1]);
};
/**
 * Every merge field a template version demands, derived from its blocks:
 * `mergeField` blocks name theirs directly; heading/paragraph/list/table/
 * callout text may embed `{{tokens}}`. Unique and sorted, so two calls over
 * the same blocks compare equal.
 *
 * This is what lets a document request decide `awaiting_data` (which fields
 * are still missing, by name) BEFORE asking documents-service to render — the
 * renderer remains the final authority, this is the early warning.
 */
function requiredMergeFields(blocks) {
    const fields = new Set();
    for (const b of blocks) {
        switch (b.type) {
            case 'mergeField':
                fields.add(b.field);
                break;
            case 'heading':
            case 'paragraph':
            case 'callout':
                tokensIn(b.text, fields);
                break;
            case 'list':
                b.items.forEach((t) => tokensIn(t, fields));
                break;
            case 'table':
                b.headers.forEach((t) => tokensIn(t, fields));
                b.rows.forEach((row) => row.forEach((t) => tokensIn(t, fields)));
                break;
            default: break; // signatureField, pageBreak carry no text
        }
    }
    return [...fields].sort();
}
/**
 * THE FOLDER PALETTE — a closed list of ids, never a hex value.
 *
 * A folder's colour is a filing cue an operator picks, and it has to render in
 * three places that do not share a stylesheet (the tree rail, the row chip, the
 * move picker). Storing `#ff0044` would make each of those invent its own
 * readable text colour against it, and half of them would get it wrong. An id
 * lets each surface look the pairing up in its own theme instead.
 *
 * ADDITIVE AND OPTIONAL: every folder written before this has none, which reads
 * as `stone` — the same neutral the tree has always drawn.
 */
exports.FOLDER_COLORS = [
    'stone', 'ruby', 'amber', 'emerald', 'sky', 'violet', 'teal', 'rose',
];
exports.FolderSchema = zod_1.z.object({
    folderId: zod_1.z.string().min(1),
    ownerScope: zod_1.z.enum(exports.OWNER_SCOPES),
    tenantId: zod_1.z.string().optional(),
    parentFolderId: zod_1.z.string().optional(),
    name: zod_1.z.string().min(1),
    sortOrder: zod_1.z.number().int().default(0),
    /** See FOLDER_COLORS. Absent reads as `stone`; nothing keys on it. */
    color: zod_1.z.enum(exports.FOLDER_COLORS).optional(),
    createdAt: zod_1.z.string(),
});
/** Slugs are permanent public URLs. Lowercase, digits and hyphens only. */
const slug = zod_1.z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/);
const withTenantWhenScoped = (schema) => schema.refine((v) => v.ownerScope !== 'tenant' || typeof v.tenantId === 'string', { message: 'tenantId is required when ownerScope is tenant', path: ['tenantId'] });
exports.DocumentSchema = withTenantWhenScoped(zod_1.z.object({
    documentId: zod_1.z.string().min(1),
    ownerScope: zod_1.z.enum(exports.OWNER_SCOPES),
    tenantId: zod_1.z.string().optional(),
    folderId: zod_1.z.string().min(1),
    slug,
    title: zod_1.z.string().min(1),
    kind: zod_1.z.enum(exports.DOCUMENT_KINDS),
    /**
     * How the reader discharges it — signed, or accepted/declined. ADDITIVE and
     * optional: absent means "whatever this `kind` always implied", which is what
     * `dischargeOf` returns, so every document written before this reads exactly
     * as it did. Setting it is how an author makes a policy signable, or an
     * agreement answerable with accept/decline.
     */
    discharge: zod_1.z.enum(exports.DOCUMENT_DISCHARGES).optional(),
    /**
     * Where this document is offered. ADDITIVE and optional: every document
     * written before categories existed has none, and an uncategorised document
     * simply does not appear in a filtered picker — it is not hidden from the
     * library, and nothing that already references it stops working.
     *
     * Validated as an ID SHAPE, not as membership of a closed list: categories
     * are rows now, and a document filed under an operator-created one must still
     * parse here. Membership is checked where a category is WRITTEN
     * (documents-service `assertKnownCategory`) — the one place that can read the
     * table — rather than in a schema that also has to parse rows written years
     * before that category existed, or after it was retired.
     */
    category: exports.DocumentCategoryIdSchema.optional(),
    /**
     * WHICH SUBJECTS THIS DOCUMENT IS USED FOR — the axis the merge-field gate
     * reasons about. A hardware deal agreement is `['deal_hardware']`; a policy
     * shown at both checkout and onboarding names both.
     *
     * This is what makes `classifyField` tell the truth. Until it existed the
     * publish gate fell through to a hardcoded `['deal_hardware']`, which lists
     * nearly every provider and so could only ever catch UNKNOWN tokens — never a
     * field that simply does not belong in this kind of document.
     *
     * OPTIONAL and additive: a document written before this field has none, and
     * the gate keeps its permissive default for those rows rather than making an
     * already-published document suddenly unpublishable. Declaring contexts is
     * how an author opts into the stricter check.
     */
    contexts: zod_1.z.array(zod_1.z.enum(context_1.MERGE_CONTEXTS)).optional(),
    /**
     * Whether this document's headings print SECTION NUMBERS, and in what shape.
     *
     * Optional and absent by default, which reads as `'none'` — exactly what
     * `RenderMeta.numbering` already defaults to, so no stored document changes
     * appearance by this field existing.
     *
     * It lives on the DOCUMENT rather than the version because numbering is a
     * house style, not an edit: switching it on should not read as a wording
     * change in the diff, and every version of one contract should number the
     * same way or a cross-reference from v3 stops meaning anything in v4.
     */
    numberingStyle: sectionNumbering_1.NumberingStyleSchema.optional(),
    /**
     * SYSTEM DOCUMENT — one of the master legal set in ./systemDocuments, created
     * by documents-service in every stage. A system document can never be
     * retired and its slug is a hard-coded public link. ADDITIVE and optional:
     * absent ⇒ an ordinary library document.
     */
    system: zod_1.z.boolean().optional(),
    /** `SystemDocumentDef.key` — present exactly when `system` is true. */
    systemKey: zod_1.z.string().min(1).optional(),
    /** Which group a system document belongs to. Absent ⇒ `rubytill`. */
    productLine: zod_1.z.enum(['rubytill', 'rubyestate', 'company']).optional(),
    /**
     * Who can read it through the public link. Absent ⇒ `public` — what every
     * published document already was. `private` needs a share token.
     */
    visibility: zod_1.z.enum(['public', 'private']).optional(),
    status: zod_1.z.enum(['active', 'retired']),
    retiredAt: zod_1.z.string().optional(),
    supersededByDocumentId: zod_1.z.string().optional(),
    createdAt: zod_1.z.string(),
}));
exports.DocumentVersionSchema = zod_1.z.object({
    documentId: zod_1.z.string().min(1),
    versionNo: zod_1.z.number().int().positive(),
    status: zod_1.z.enum(exports.VERSION_STATUSES),
    blocks: zod_1.z.array(exports.DocumentBlockSchema),
    /**
     * THE DOCUMENT AS ONE CONTINUOUS FLOW — the successor to `blocks`.
     *
     * OPTIONAL, and additive on purpose. Every version stored before this
     * existed carries `blocks` and no `delta`, and renders exactly as it always
     * has; nothing migrates and nothing is rewritten.
     *
     * WHEN `delta` IS PRESENT IT IS AUTHORITATIVE and `blocks` is dead weight
     * kept only so an already-published row still parses. Nothing derives one
     * from the other at read time: a published version's bytes are hashed and
     * bound to a signature, so it must render from what it was actually stored
     * as, not from a re-conversion that a later converter change could alter.
     *
     * See the `hasBody` refine below — a version with neither is a blank
     * contract, which is the one outcome this subsystem exists to prevent.
     */
    delta: documentDelta_1.DeltaDocumentSchema.optional(),
    effectiveAt: zod_1.z.string().optional(),
    changeType: zod_1.z.enum(exports.CHANGE_TYPES).optional(),
    changeSummary: zod_1.z.string().optional(),
    createdBy: zod_1.z.string().min(1),
    createdAt: zod_1.z.string(),
    approvedBy: zod_1.z.string().optional(),
    approvedAt: zod_1.z.string().optional(),
    selfApproved: zod_1.z.boolean().optional(),
    publishedBy: zod_1.z.string().optional(),
    publishedAt: zod_1.z.string().optional(),
    /**
     * WHO, IN WORDS — the acting user's email at the time they acted.
     *
     * The `*By` fields above stay the Cognito `sub`, because that is the stable
     * identity the separation-of-duties check compares and an email is mutable.
     * These carry the human-readable half so a version chain can say
     * "Approved by dana@rubytill.com" instead of a UUID nobody can place.
     *
     * ALL OPTIONAL and additive: every version stored before this existed simply
     * has none, and the UI falls back to the id. A SNAPSHOT, not a lookup — it
     * records the address that acted, which is what an audit trail needs, and
     * does not silently change if the person's email changes later.
     */
    createdByEmail: zod_1.z.string().optional(),
    submittedByEmail: zod_1.z.string().optional(),
    approvedByEmail: zod_1.z.string().optional(),
    publishedByEmail: zod_1.z.string().optional(),
    rejectedByEmail: zod_1.z.string().optional(),
    renderedHtmlSha256: zod_1.z.string().optional(),
    renderedPdfSha256: zod_1.z.string().optional(),
    rendererVersion: zod_1.z.string().optional(),
    // The frozen artifacts produced at publish, one per format. MUST be declared
    // here: Zod strips unknown keys, so a version row parsed through this schema
    // without this field would silently lose its entire artifact list — and with
    // it the hashes the evidence chain depends on.
    artifacts: zod_1.z.array(documentAcceptance_1.VersionArtifactSchema).optional(),
    // Merge values resolved at publish and FROZEN, exactly as an envelope freezes
    // its own (§4.5). Without these the artifact cannot be reproduced, and a hash
    // that cannot be reproduced verifies nothing.
    mergeData: zod_1.z.record(zod_1.z.string()).optional(),
})
    .refine((v) => new Set(v.blocks.map((b) => b.id)).size === v.blocks.length, {
    message: 'block ids must be unique within a version', path: ['blocks'],
})
    /**
     * A version must actually SAY something. Without this a Delta version whose
     * `delta` failed to write would parse happily as `blocks: []` and render as
     * a blank document — a contract that looks valid, hashes fine, and contains
     * nothing. The empty-`blocks` shape is legal only when a `delta` supplies
     * the body.
     *
     * EXCEPT WHILE IT IS A DRAFT. Every document starts as an empty v1 — the
     * create transaction writes `blocks: []` with no `delta` because nobody has
     * typed anything yet, and the editor opens exactly that shape as a blank
     * Delta. Applying the rule to drafts made creating a document impossible
     * (`invalid_version`, 400). The guard belongs where a blank body is actually
     * dangerous: from review onwards, and above all at publish, which is what
     * binds bytes to a signature.
     */
    .refine((v) => v.status === 'draft' || v.blocks.length > 0 || v.delta !== undefined, {
    message: 'a version must have blocks or a delta — one of the two carries the document body',
    path: ['delta'],
})
    .refine((v) => v.status !== 'published' || typeof v.effectiveAt === 'string', {
    message: 'a published version must have an effectiveAt', path: ['effectiveAt'],
})
    .refine((v) => v.status !== 'published' || typeof v.changeType === 'string', {
    message: 'a published version must declare material or minor — there is no default',
    path: ['changeType'],
})
    .refine((v) => !(v.status === 'published' && v.changeType === 'material') ||
    (typeof v.changeSummary === 'string' && v.changeSummary.trim().length > 0), { message: 'a material change must carry a changeSummary shown to users', path: ['changeSummary'] });
//# sourceMappingURL=documents.js.map